import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageQuery, SortOrder, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { NameDto } from '@model/NameDto'
import { RebateTransactionDto, RebateTransactionSortKeys } from '@model/RebateTransactionDto'

import { FilterQueryProps } from '@global/filter/FilterQueryModal'

import { IAppendableFetchResult, useFetchAppendablePage } from '@hooks/useFetch'

interface IBRebateTransaction {
  query: IAppendableFetchResult<RebateTransactionDto[]>
  sortOptions: NameDto<RebateTransactionSortKeys | null>[]
  search?: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  clearSearch: (callback: () => void) => void
}

export const useIBRebateTransactions = (): IBRebateTransaction => {
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const [search, setSearch] = useState<FilterQueryProps>()

  const sortOptions: NameDto<RebateTransactionSortKeys | null>[] = [
    {
      id: 'transactionDate',
      name: t('Date'),
    },
    {
      id: 'tradingAccountName',
      name: t('Trading Account.Trading Account'),
    },
    {
      id: 'accountType',
      name: t('Account Type'),
    },
    {
      id: 'accumulatedVolumeFxm',
      name: t('IB.RebatesPage.ClientsPage.Vol. FXM'),
    },
    {
      id: 'accumulatedVolumeOther',
      name: t('IB.RebatesPage.ClientsPage.Vol. Other'),
    },
    {
      id: 'accumulatedCommission',
      name: t('Commission'),
    },
    {
      id: 'rebateAmount',
      name: t('IB.RebatesPage.GroupsPage.Rebate'),
    },
    {
      id: 'state',
      name: t('Status'),
    },
  ]

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  const defaultSort = 'transactionDate'
  const defaultSortOrder: SortOrder = 'DESC'

  const accountSummaryCallback = useCallback(
    async (query?: PageQuery) =>
      await apiClient.getIBRebateTransaction(account?.clientIntroducingBroker?.id ?? '', {
        ...query,
        search: search,
        caller: 'ca',
        languageId: locale,
        sort: query?.sort ?? defaultSort,
        sortOrder: query?.sortOrder ?? defaultSortOrder,
        dismissNotifications: true,
      }),
    [apiClient, account?.clientIntroducingBroker?.id, search, locale, defaultSort, defaultSortOrder]
  )
  const query = useFetchAppendablePage(accountSummaryCallback)

  return {
    query,
    sortOptions,
    search,
    setSearch,
    clearSearch,
  }
}
